import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const STATUS_COLORS = {
  Draft: 'bg-gray-500/15 text-gray-400',
  Active: 'bg-emerald-500/15 text-emerald-400',
  Completed: 'bg-blue-500/15 text-blue-400',
  Terminated: 'bg-red-500/15 text-red-400',
};

export default function ContractsPage() {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const [form, setForm] = useState({
    customer_name: '', start_date: '', end_date: '',
    billing_cycle: 'Monthly', notes: '', selectedAssets: [],
  });
  const [assetRates, setAssetRates] = useState({});

  const fetchData = async () => {
    try {
      const [cRes, custRes, aRes] = await Promise.all([
        api.get('/contracts'),
        api.get('/customers'),
        api.get('/assets'),
      ]);
      setContracts(cRes.data);
      setCustomers(Array.isArray(custRes.data) ? custRes.data : []);
      setAssets(Array.isArray(aRes.data) ? aRes.data : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const availableAssets = assets.filter(a => {
    const s = a.asset_status === 'Active' ? 'Available' : a.asset_status;
    return s === 'Available';
  });

  const handleCreate = async () => {
    if (!form.customer_name || !form.start_date || !form.end_date) {
      toast.error('Please fill all required fields'); return;
    }
    if (form.selectedAssets.length === 0) {
      toast.error('Please select at least one asset'); return;
    }
    try {
      const res = await api.post('/contracts', {
        customer_name: form.customer_name,
        start_date: form.start_date,
        end_date: form.end_date,
        billing_cycle: form.billing_cycle,
        notes: form.notes,
        lines: form.selectedAssets.map(aid => ({
          asset_id: aid,
          monthly_rate: assetRates[aid] || null,
        })),
      });
      toast.success(`Contract ${res.data.contract_number} created`);
      setShowDialog(false);
      setForm({ customer_name: '', start_date: '', end_date: '', billing_cycle: 'Monthly', notes: '', selectedAssets: [] });
      setAssetRates({});
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error creating contract'); }
  };

  const toggleAsset = (assetId) => {
    setForm(prev => ({
      ...prev,
      selectedAssets: prev.selectedAssets.includes(assetId)
        ? prev.selectedAssets.filter(id => id !== assetId)
        : [...prev.selectedAssets, assetId],
    }));
  };

  const filtered = contracts.filter(c => {
    if (statusFilter !== 'all' && c.status !== statusFilter) return false;
    if (searchTerm && !c.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) && !c.contract_number.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  if (loading) return <div className="flex justify-center items-center h-64"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-6" data-testid="contracts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Contracts</h1>
          <p className="text-muted-foreground text-sm">Manage rental contracts and asset allocations</p>
        </div>
        <Button onClick={() => setShowDialog(true)} data-testid="create-contract-btn"><Plus className="h-4 w-4 mr-2" />New Contract</Button>
      </div>

      <div className="flex gap-3 items-center flex-wrap">
        <Input placeholder="Search contracts..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="max-w-xs" data-testid="contract-search" />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="Draft">Draft</SelectItem>
            <SelectItem value="Active">Active</SelectItem>
            <SelectItem value="Completed">Completed</SelectItem>
            <SelectItem value="Terminated">Terminated</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">{filtered.length} contracts</span>
      </div>

      <div className="grid gap-4">
        {filtered.map(c => (
          <Card key={c.id} className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(`/contracts/${c.id}`)} data-testid={`contract-card-${c.contract_number}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-primary" />
                    <span className="font-bold font-mono">{c.contract_number}</span>
                    <Badge variant="outline" className={STATUS_COLORS[c.status]}>{c.status}</Badge>
                  </div>
                  <div className="text-lg font-semibold">{c.customer_name}</div>
                  <div className="text-sm text-muted-foreground">{c.start_date} to {c.end_date} &middot; {c.billing_cycle}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-muted-foreground">Monthly Value</div>
                  <div className="text-xl font-bold text-primary">₹{(c.total_monthly_value || 0).toLocaleString()}</div>
                  <div className="text-xs text-muted-foreground">{(c.contract_lines || []).filter(l => l.status === 'Active').length} active assets</div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {filtered.length === 0 && <div className="text-center py-12 text-muted-foreground">No contracts found. Create your first contract.</div>}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Create New Contract</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Customer *</Label>
                <Select value={form.customer_name} onValueChange={v => setForm({ ...form, customer_name: v })}>
                  <SelectTrigger><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {customers.slice(0, 200).map(c => <SelectItem key={c.id} value={c.billing_name}>{c.billing_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Billing Cycle</Label>
                <Select value={form.billing_cycle} onValueChange={v => setForm({ ...form, billing_cycle: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Monthly">Monthly</SelectItem>
                    <SelectItem value="Quarterly">Quarterly</SelectItem>
                    <SelectItem value="Annual">Annual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Start Date *</Label>
                <Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} />
              </div>
              <div>
                <Label>End Date *</Label>
                <Input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Contract notes..." />
            </div>
            <div>
              <Label>Select Assets ({form.selectedAssets.length} selected)</Label>
              <div className="border rounded-lg max-h-48 overflow-y-auto mt-1">
                {availableAssets.length === 0 ? (
                  <div className="p-3 text-muted-foreground text-sm">No available assets</div>
                ) : availableAssets.map(a => (
                  <div key={a.asset_id} className={`flex items-center justify-between p-2 border-b last:border-0 cursor-pointer hover:bg-muted/50 ${form.selectedAssets.includes(a.asset_id) ? 'bg-primary/5' : ''}`}
                    onClick={() => toggleAsset(a.asset_id)}>
                    <div className="flex items-center gap-2">
                      <input type="checkbox" checked={form.selectedAssets.includes(a.asset_id)} readOnly className="rounded" />
                      <span className="font-mono text-xs">{a.asset_id}</span>
                      <span className="text-sm">{a.brand} {a.model_name}</span>
                      <Badge variant="outline" className="text-xs">{a.asset_type}</Badge>
                    </div>
                    {form.selectedAssets.includes(a.asset_id) && (
                      <Input type="number" placeholder="Rate/mo" className="w-28 h-7 text-xs"
                        value={assetRates[a.asset_id] || ''}
                        onClick={e => e.stopPropagation()}
                        onChange={e => { e.stopPropagation(); setAssetRates({ ...assetRates, [a.asset_id]: e.target.value ? Number(e.target.value) : undefined }); }} />
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button onClick={handleCreate} data-testid="submit-contract-btn">Create Contract</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
